import { Button } from "@/components/button";
import { api } from "@/modules/api";
import { fileModule } from "@/modules/file";
import { UploadedImage } from "@/components/uploaded-image";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";
import { atlasRoutes } from "@/app/router/routes";

export const CompanyProfilePage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const profile = useQuery({
    queryKey: ["my-staff-profile"],
    queryFn: () => api.get("/internal/v1/me/staff/member", {}),
  });

  const member = profile.data?.data;

  const [displayName, setDisplayName] = useState("");
  const [aboutMe, setAboutMe] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoUploadedFileId, setPhotoUploadedFileId] = useState<string | null>(null);
  const [newPhotoUploaded, setNewPhotoUploaded] = useState(false);
  const [location, setLocation] = useState("");

  const [initialValues, setInitialValues] = useState({
    displayName: "",
    aboutMe: "",
    photoUploadedFileId: null as string | null,
    location: "",
  });

  useEffect(() => {
    if (member) {
      const initial = {
        displayName: member.profile_display_name,
        aboutMe: member.profile_about_me ?? "",
        photoUploadedFileId: member.profile_photo_uploaded_file_id ?? null,
        location: member.profile_current_location ?? "",
      };
      setInitialValues(initial);
      setDisplayName(initial.displayName);
      setAboutMe(initial.aboutMe);
      setPhotoUploadedFileId(initial.photoUploadedFileId);
      setLocation(initial.location);
      setNewPhotoUploaded(false);
    }
  }, [member]);

  const handleUploadPhoto = async () => {
    if (!photoFile) return;
    const { fileId } = await fileModule.uploadFile({ file: photoFile });
    setPhotoUploadedFileId(fileId);
    setNewPhotoUploaded(true);
    setPhotoFile(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!member) return;

    const updates: Record<string, unknown> = {};

    if (displayName !== initialValues.displayName) {
      updates.profile_display_name = displayName;
    }

    if (aboutMe !== initialValues.aboutMe) {
      updates.profile_about_me = aboutMe || null;
    }

    if (photoUploadedFileId !== initialValues.photoUploadedFileId) {
      updates.profile_photo_uploaded_file_id = photoUploadedFileId ?? null;
    }

    if (location !== initialValues.location) {
      updates.profile_current_location = location ?? null;
    }

    if (Object.keys(updates).length === 0) {
      return;
    }

    await api.put("/internal/v1/staff/members/{staff_member_id}", {
      params: { path: { staff_member_id: member.id } },
      body: updates,
    });
    queryClient.invalidateQueries({ queryKey: ["my-staff-profile"] });
    setNewPhotoUploaded(false);
  };

  if (profile.data?.error) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return <div>{JSON.stringify(profile.data.error, null, 2)}</div>;
  }

  return (
    <Container>
      <Header>
        <h1>My Profile</h1>
        {member && (
          <Button
            variant="outline"
            onClick={() =>
              navigate(atlasRoutes.companyTeamMember({ params: { staffMemberId: member.id } }).path)
            }
          >
            View My Public Profile
          </Button>
        )}
      </Header>
      {member ? (
        <Form onSubmit={handleSave}>
          <FormField>
            <Label>Display Name</Label>
            <Input value={displayName} onChange={e => setDisplayName(e.target.value)} />
          </FormField>

          <FormField>
            <Label>About Me</Label>
            <TextArea value={aboutMe} onChange={e => setAboutMe(e.target.value)} rows={4} />
          </FormField>

          <FormField>
            <Label>Location</Label>
            <Input value={location} onChange={e => setLocation(e.target.value)} />
          </FormField>

          <FormField>
            <Label>Profile Photo</Label>
            {photoUploadedFileId && (
              <CurrentPhotoWrapper>
                <UploadedImage uploadedFileId={photoUploadedFileId} size={100} />
                <PhotoInfo>Current profile photo</PhotoInfo>
              </CurrentPhotoWrapper>
            )}
            <FileInput
              type="file"
              accept="image/*"
              onChange={e => {
                setPhotoFile(e.target.files?.[0] ?? null);
                setNewPhotoUploaded(false);
              }}
            />
            {photoFile && (
              <Button type="button" onClick={handleUploadPhoto} style={{ marginTop: "8px" }}>
                Upload New Photo
              </Button>
            )}
            {newPhotoUploaded && (
              <UploadStatus>New photo uploaded - Save changes to apply</UploadStatus>
            )}
          </FormField>

          <SubmitButton type="submit">Save Changes</SubmitButton>
        </Form>
      ) : (
        <div>Loading...</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  h1 {
    margin: 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  font-size: 14px;
  color: #333;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const FileInput = styled.input`
  padding: 4px;
  font-size: 14px;
`;

const UploadStatus = styled.span`
  color: #28a745;
  font-size: 14px;
  margin-top: 8px;
`;

const SubmitButton = styled(Button)`
  margin-top: 16px;
  align-self: flex-start;
`;

const CurrentPhotoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const PhotoInfo = styled.span`
  font-size: 12px;
  color: #666;
`;
